import {
  ChangeDetectionStrategy, Component, ElementRef, WritableSignal,
  computed, effect, inject, input, signal, untracked, viewChild
} from '@angular/core';
import { UpperCasePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse, HttpEventType } from '@angular/common/http';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';

import { ButtonComponent } from '@libs/front/components/button/button.component';
import { InputComponent } from '@libs/front/components/input/input.component';
import { PopupComponent } from '@libs/front/components/popup/popup.component';

import { Dossier } from 'src/app/bo/dossier';
import { DirectoryType, Fichier } from 'src/app/bo/fichier';
import { DocumentStorageService } from 'src/app/services/document-storage.service';
import { FonctionsService } from 'src/app/services/fonctions-partagees.service';
import { HttpService } from 'src/app/services/http.service';
import { KeycloakUtilisateurService } from 'src/app/services/keycloak/keycloak-utilisateur.service';


const SITE_END_POINT = 'Sites';
const PROJECT_END_POINT = 'Projects';
const PREVIEWABLE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'pdf'];
const FORBIDDEN_NAME_CHARS = ['/', '\\', ':', '*', '?', '"', '<', '>', '|', '.'];
const MAX_NAME_LENGTH = 50;

class FileFolder {
  fichiers: Fichier[];
  dossiers: Dossier[];
  id: number;
  apiEndPoint: string;
}

@Component({
  selector: 'app-site-files',
  standalone: true,
  templateUrl: './site-files.component.html',
  styleUrls: ['./site-files.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    UpperCasePipe,
    TranslateModule,
    ButtonComponent,
    InputComponent,
    PopupComponent,
  ],
})
export class SiteFilesComponent {
  readonly siteId    = input<number | null>(null);
  readonly siteName  = input<string>('');
  /** Renseigné sur l'écran projet : les fichiers du projet remplacent alors ceux du site. */
  readonly projectId = input<number | null>(null);

  private readonly http                   = inject(HttpService);
  private readonly documentStorageService = inject(DocumentStorageService);
  private readonly fonctionsService       = inject(FonctionsService);
  private readonly translate              = inject(TranslateService);
  private readonly keycloakUserService    = inject(KeycloakUtilisateurService);

  private readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  /** Un invité n'a que la lecture sur ces fichiers, comme dans l'ancien explorateur. */
  protected readonly canWrite = !this.keycloakUserService.isGuest;

  // ── Arborescence ──────────────────────────────────────────────────────────

  protected readonly apiEndPoint = computed(() => this.projectId() != null ? PROJECT_END_POINT : SITE_END_POINT);
  private readonly ownerId       = computed(() => this.projectId() ?? this.siteId());

  protected readonly root           = signal<Dossier | null>(null);
  protected readonly loading         = signal(false);
  protected readonly currentFolderId = signal<number | null>(null);
  protected readonly currentFolder   = computed(() => {
    const root = this.root();
    const id = this.currentFolderId();
    return root && id != null ? this.findFolder(root, id) ?? root : root;
  });
  protected readonly path = computed<Dossier[]>(() => {
    const root = this.root();
    const current = this.currentFolder();
    return root && current ? this.buildPath(root, current.dossierId) ?? [root] : [];
  });

  // ── Tri ───────────────────────────────────────────────────────────────────

  protected readonly folders = computed<Dossier[]>(() =>
    [...(this.currentFolder()?.sousDossiers ?? [])].sort((a, b) => this.compareStrings(a.nom, b.nom))
  );
  protected readonly files = computed<Fichier[]>(() =>
    [...(this.currentFolder()?.fichiers ?? [])].sort((a, b) => this.compareStrings(a.nom, b.nom))
  );
  protected readonly isEmpty = computed(() => this.folders().length === 0 && this.files().length === 0);

  protected readonly currentFolderSize = computed(() => {
    const folder = this.currentFolder();
    return folder ? this.computeFolderSize(folder) : 0;
  });

  // ── Popups ────────────────────────────────────────────────────────────────

  protected readonly showRenamePopup    = signal(false);
  protected readonly newName            = signal('');
  protected readonly renameNameError    = signal(false);
  protected readonly renameNameRequired = signal(false);

  protected readonly pendingDelete = signal<{ file: Fichier | null; folder: Dossier | null } | null>(null);

  protected readonly pendingUpload       = signal<File[]>([]);
  protected readonly uploadHasDuplicates = signal(false);
  protected readonly uploadTotalSize     = computed(() => this.pendingUpload().reduce((sum, file) => sum + file.size, 0));
  protected readonly showUploadProgress  = signal(false);
  protected readonly uploadProgress      = signal(0);
  protected readonly dragOver            = signal(false);

  protected readonly downloadInProgress = signal(false);
  protected readonly showSyncError      = signal(false);

  private renamingFile: Fichier | null = null;
  private renamingFolder: Dossier | null = null;
  private dragCounter = 0;

  constructor() {
    effect(() => {
      const ownerId = this.ownerId();
      const endPoint = this.apiEndPoint();
      if (ownerId) {
        untracked(() => this.loadRoot(`${endPoint}/${ownerId}`));
      }
    });
  }

  // ── Chargement ────────────────────────────────────────────────────────────

  /** Un changement de site ou de projet ramène à la racine ; un simple rechargement conserve le dossier courant. */
  private loadRoot(baseUrl: string): void {
    this.loading.set(true);
    this.root.set(null);
    this.http.get<Dossier>(`${baseUrl}/Folder`).subscribe({
      next: folder => {
        this.loading.set(false);
        this.root.set(folder);
        this.currentFolderId.set(folder?.dossierId ?? null);
      },
      error: (error: HttpErrorResponse) => {
        this.loading.set(false);
        if (error.status === 401) {
          this.translate.get('GLOBAL.NON_AUTORISE').subscribe(trad => this.fonctionsService.launch_toast(false, false, trad));
        }
      },
    });
  }

  private reloadRoot(): void {
    this.http.get<Dossier>(`${this.baseUrl()}/Folder`).subscribe({
      next: folder => this.applyRoot(folder),
      error: (error: HttpErrorResponse) => console.error('Error reloading folder:', error),
    });
  }

  /** Chaque opération renvoie la racine à jour ; le dossier courant est conservé s'il existe encore. */
  private applyRoot(folder: Dossier | null): void {
    if (!folder) return;
    this.root.set(folder);
    if (this.currentFolderId() == null || !this.findFolder(folder, this.currentFolderId()!)) {
      this.currentFolderId.set(folder.dossierId);
    }
  }

  private baseUrl(): string {
    return `${this.apiEndPoint()}/${this.ownerId()}`;
  }

  // ── Navigation ────────────────────────────────────────────────────────────

  protected openFolder(folder: Dossier): void {
    this.currentFolderId.set(folder.dossierId);
  }

  protected goToParent(): void {
    const path = this.path();
    if (path.length > 1) this.openFolder(path[path.length - 2]);
  }

  private findFolder(folder: Dossier, folderId: number): Dossier | null {
    if (folder.dossierId === folderId) return folder;
    for (const subFolder of folder.sousDossiers ?? []) {
      const found = this.findFolder(subFolder, folderId);
      if (found) return found;
    }
    return null;
  }

  private buildPath(folder: Dossier, folderId: number): Dossier[] | null {
    if (folder.dossierId === folderId) return [folder];
    for (const subFolder of folder.sousDossiers ?? []) {
      const subPath = this.buildPath(subFolder, folderId);
      if (subPath) return [folder, ...subPath];
    }
    return null;
  }

  // ── Tri ───────────────────────────────────────────────────────────────────

  private compareStrings(a: string, b: string): number {
    return (a || '').toLowerCase().localeCompare((b || '').toLowerCase());
  }

  // ── Renommage ─────────────────────────────────────────────────────────────

  private validateName(value: string, errorFlag: WritableSignal<boolean>, requiredFlag: WritableSignal<boolean>): boolean {
    requiredFlag.set(value == null || value.trim() === '');
    errorFlag.set([...(value ?? '')].some(char => FORBIDDEN_NAME_CHARS.includes(char)));
    return !requiredFlag() && !errorFlag();
  }

  protected limitName(value: string): string {
    return (value ?? '').slice(0, MAX_NAME_LENGTH);
  }

  protected renameFile(file: Fichier): void {
    this.openRename(file, null, file.nom.split('.')[0]);
  }

  protected renameFolder(folder: Dossier): void {
    this.openRename(null, folder, folder.nom);
  }

  private openRename(file: Fichier | null, folder: Dossier | null, name: string): void {
    this.renamingFile   = file;
    this.renamingFolder = folder;
    this.newName.set(name);
    this.renameNameError.set(false);
    this.renameNameRequired.set(false);
    this.showRenamePopup.set(true);
  }

  /** En cas d'échec, l'élément a pu être supprimé entre-temps : on resynchronise et on prévient l'utilisateur. */
  protected confirmRename(): void {
    const name = this.newName();
    if (!this.validateName(name, this.renameNameError, this.renameNameRequired)) return;

    const request: Observable<Dossier> = this.renamingFile
      ? this.http.put<string, Dossier>(`${this.baseUrl()}/Files/${this.renamingFile.fichierId}`, name)
      : this.http.put<string, Dossier>(`${this.baseUrl()}/Folders/${this.renamingFolder!.dossierId}`, name);

    this.fonctionsService.loading_screen(true);
    request.subscribe({
      next: folder => {
        this.fonctionsService.loading_screen(false);
        this.closeRename();
        this.applyRoot(folder);
      },
      error: () => {
        this.fonctionsService.loading_screen(false);
        this.closeRename();
        this.reloadRoot();
        this.showSyncError.set(true);
      },
    });
  }

  protected closeRename(): void {
    this.showRenamePopup.set(false);
    this.renamingFile   = null;
    this.renamingFolder = null;
  }

  // ── Suppression ───────────────────────────────────────────────────────────

  protected deleteFile(file: Fichier): void {
    this.pendingDelete.set({ file, folder: null });
  }

  protected deleteFolder(folder: Dossier): void {
    this.pendingDelete.set({ file: null, folder });
  }

  /** La réponse renvoie la racine à jour. */
  protected confirmDelete(): void {
    const pending = this.pendingDelete();
    if (!pending) return;

    const request: Observable<Dossier> = pending.file
      ? this.http.post<Fichier[], Dossier>(`${this.baseUrl()}/Files/Delete`, [pending.file])
      : this.http.post<Dossier[], Dossier>(`${this.baseUrl()}/Folders/Delete`, [pending.folder!]);

    this.fonctionsService.loading_screen(true);
    request.subscribe({
      next: folder => {
        this.fonctionsService.loading_screen(false);
        this.pendingDelete.set(null);
        this.applyRoot(folder);
      },
      error: () => {
        this.fonctionsService.loading_screen(false);
        this.pendingDelete.set(null);
      },
    });
  }

  // ── Ajout de fichiers ─────────────────────────────────────────────────────

  protected triggerFileInput(): void {
    this.fileInput()?.nativeElement.click();
  }

  protected onFileInputChange(event: Event): void {
    const fileList = (event.target as HTMLInputElement).files;
    if (fileList?.length) this.prepareUpload(fileList);
  }

  protected onDragEnter(event: DragEvent): void {
    event.preventDefault();
    if (this.dragCounter++ === 0) this.dragOver.set(true);
  }

  protected onDragOver(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  }

  protected onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dragCounter = Math.max(0, this.dragCounter - 1);
    if (this.dragCounter === 0) this.dragOver.set(false);
  }

  protected onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragCounter = 0;
    this.dragOver.set(false);

    const fileList = event.dataTransfer?.files;
    if (!fileList?.length) return;
    if (!this.canWrite) {
      this.translate.get('GLOBAL.NON_AUTORISE_SAVE').subscribe(trad => this.fonctionsService.launch_toast(false, false, trad));
      return;
    }
    this.prepareUpload(fileList);
  }

  /** Les fichiers déjà présents dans le dossier courant (extension comparée en minuscules) seront écrasés : on prévient. */
  private prepareUpload(fileList: FileList): void {
    const files = Array.from(fileList);
    const existing = this.currentFolder()?.fichiers ?? [];
    this.uploadHasDuplicates.set(files.some(file => existing.some(f => f.nom === this.normalizeExtension(file.name))));
    this.pendingUpload.set(files);
  }

  protected cancelUpload(): void {
    this.pendingUpload.set([]);
    this.clearFileInput();
  }

  protected confirmUpload(): void {
    const files = this.pendingUpload();
    const folder = this.currentFolder();
    if (files.length === 0 || !folder) return;

    const formData = new FormData();
    files.forEach(file => formData.append(file.name, file));
    formData.append('dossierId', folder.dossierId.toString());

    this.pendingUpload.set([]);
    this.uploadProgress.set(0);
    this.showUploadProgress.set(true);

    this.http.request('POST', `${this.baseUrl()}/Files/Upload?siteId=${this.siteId()}`, formData).subscribe({
      next: (event: any) => {
        if (event.type === HttpEventType.UploadProgress && event.total) {
          this.uploadProgress.set(Math.trunc(event.loaded / event.total * 100));
        }
        if (event.type === HttpEventType.Response) {
          this.showUploadProgress.set(false);
          this.clearFileInput();
          this.applyRoot(event.body as Dossier);
        }
      },
      error: () => {
        this.showUploadProgress.set(false);
        this.clearFileInput();
      },
    });
  }

  private clearFileInput(): void {
    const input = this.fileInput()?.nativeElement;
    if (input) input.value = '';
  }

  // ── Téléchargement ────────────────────────────────────────────────────────

  /** « Download » récupère tout le dossier courant ; un dossier ne contenant qu'un fichier le télécharge tel quel. */
  protected downloadAll(): void {
    const folder = this.currentFolder();
    if (!folder || this.isEmpty()) return;
    this.download(this.files(), this.folders(), folder.nom);
  }

  protected downloadFolder(folder: Dossier): void {
    this.download([], [folder], folder.nom);
  }

  protected downloadFile(file: Fichier): void {
    this.download([file], [], file.nom);
  }

  private download(files: Fichier[], folders: Dossier[], archiveName: string): void {
    if (files.length === 1 && folders.length === 0) {
      this.openFile(files[0]);
      return;
    }

    const body = new FileFolder();
    body.fichiers = files;
    body.dossiers = folders;
    const fileCategory = this.documentStorageService.getCurrentFileCategory(this.apiEndPoint());

    this.downloadInProgress.set(true);
    this.http.downloadFilePost(`Files?fileCategory=${fileCategory}`, body).subscribe({
      next: (result: BlobPart) => {
        this.triggerDownload(new Blob([result], { type: 'application/zip' }), this.fonctionsService.buildArchiveName(null, archiveName));
        this.downloadInProgress.set(false);
      },
      error: () => {
        this.downloadInProgress.set(false);
        this.fonctionsService.launch_toast(false, false, '');
      },
    });
  }

  /**
   * Les documents à la racine d'un site passent par OpenDocumentsSite, comme dans l'ancien explorateur
   * (racine portant le nom du site).
   */
  private openFile(file: Fichier): void {
    this.fonctionsService.loading_screen(true);

    if (file.storedInS3) {
      this.documentStorageService.getDownloadUrl(this.getS3Key(file), file.fichierId, file.siteId, DirectoryType.USERFILES).subscribe({
        next: result => {
          if (result?.downloadUrl) window.open(result.downloadUrl, '_blank');
          this.fonctionsService.loading_screen(false);
        },
        error: (error: HttpErrorResponse) => this.handleError(error),
      });
      return;
    }

    const fileFolder = new FileFolder();
    fileFolder.fichiers = [file];
    this.documentStorageService.checkFilesExist(fileFolder).subscribe({
      next: exists => {
        if (!exists) {
          this.fonctionsService.launch_toast(false, false, 'File not found');
          this.fonctionsService.loading_screen(false);
          return;
        }
        const query = this.projectId() == null && this.root()?.nom === this.siteName()
          ? `Files/${file.fichierId}/OpenDocumentsSite/${file.siteId}`
          : `Files/${file.fichierId}/Open/${file.siteId}?fileType=${DirectoryType.USERFILES}`;
        this.http.downloadFile(query).subscribe({
          next: response => {
            if (!response) {
              this.fonctionsService.launch_toast(false, false, 'Error during file download');
              this.fonctionsService.loading_screen(false);
              return;
            }
            this.triggerDownload(new Blob([response.body], { type: 'base64' }), file.nom);
            setTimeout(() => this.fonctionsService.loading_screen(false), 1000);
          },
          error: (error: HttpErrorResponse) => this.handleError(error),
        });
      },
      error: (error: HttpErrorResponse) => this.handleError(error),
    });
  }

  private triggerDownload(blob: Blob, fileName: string): void {
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => link.remove(), 100);
  }

  protected openFileInNewWindow(file: Fichier, event: Event): void {
    event.stopPropagation();

    const notifyNotFound = (error: HttpErrorResponse) => {
      if (error.status === 404) {
        this.translate.get('GLOBAL.NO_FILE_FOUND').subscribe(trad => this.fonctionsService.launch_toast(false, false, trad));
      }
    };

    if (file.storedInS3) {
      this.documentStorageService.getDownloadUrl(this.getS3Key(file), file.fichierId, file.siteId, DirectoryType.USERFILES).subscribe({
        next: result => { if (result?.downloadUrl) window.open(result.downloadUrl, '_blank'); },
        error: notifyNotFound,
      });
      return;
    }

    this.documentStorageService.openFileInNewWindow(`Files/${file.fichierId}/Open/${file.siteId}/?fileType=${DirectoryType.USERFILES}`).subscribe({
      next: blob => window.open(URL.createObjectURL(blob), '_blank'),
      error: notifyNotFound,
    });
  }

  private getS3Key(file: Fichier): string {
    return `${file.siteId}/${this.documentStorageService.getS3Folder(this.apiEndPoint())}/${file.dossierId}/${file.nom}`;
  }

  private handleError(error: HttpErrorResponse): void {
    console.error('Error:', error);
    this.fonctionsService.launch_toast(false, false, 'An error occurred');
    this.fonctionsService.loading_screen(false);
  }

  // ── Affichage ─────────────────────────────────────────────────────────────

  protected canPreview(file: Fichier): boolean {
    return PREVIEWABLE_EXTENSIONS.includes(this.getFileExtension(file.nom).toLowerCase());
  }

  protected getFileExtension(fileName: string): string {
    return fileName.slice((fileName.lastIndexOf('.') - 1 >>> 0) + 2);
  }

  protected countFiles(folder: Dossier): number {
    let count = folder.fichiers?.length ?? 0;
    folder.sousDossiers?.forEach(subFolder => count += this.countFiles(subFolder));
    return count;
  }

  private computeFolderSize(folder: Dossier): number {
    let size = (folder.fichiers ?? []).reduce((sum, file) => sum + (file.taille ?? 0), 0);
    folder.sousDossiers?.forEach(subFolder => size += this.computeFolderSize(subFolder));
    return size;
  }

  protected formatFileSize(sizeInBytes: number): string {
    if (!sizeInBytes) return '0Mo';
    return Math.round(sizeInBytes / (1024 * 1024)) + 'Mo';
  }

  protected formatFileDate(date: Date): string {
    if (!date) return '';
    const d = new Date(date);
    return `${this.pad(d.getDate())}/${this.pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  }

  /** Infobulle des métadonnées : l'heure de dépôt et l'auteur, affichés en colonne dans l'ancien explorateur. */
  protected getFileTooltip(file: Fichier): string {
    if (!file.dateCreation) return file.uploadedByUsername ?? '';
    const d = new Date(file.dateCreation);
    const dateTime = `${this.formatFileDate(file.dateCreation)} ${this.pad(d.getHours())}:${this.pad(d.getMinutes())}`;
    return file.uploadedByUsername ? `${dateTime} - ${file.uploadedByUsername}` : dateTime;
  }

  private pad(value: number): string {
    return value.toString().padStart(2, '0');
  }

  private normalizeExtension(fileName: string): string {
    const dotIndex = fileName.lastIndexOf('.');
    return dotIndex === -1 ? fileName : fileName.substring(0, dotIndex) + fileName.substring(dotIndex).toLowerCase();
  }
}
